import React, { useState, useEffect } from 'react';
import {
  MdSecurity,
  MdInfo,
  MdGavel,
  MdClose,
  MdOutlineEmail,
  MdOutlineLocationOn,
  MdVerified,
} from 'react-icons/md';
import './Footer.css';

export default function Footer({ onNavigate }) {
  const [activeModalTab, setActiveModalTab] = useState(null); // 'about' | 'privacy' | 'terms' | null

  // Listen to global event so sidebar or any other button can open legal modal
  useEffect(() => {
    const handleOpenModal = (e) => {
      const tab = e.detail?.tab || 'about';
      setActiveModalTab(tab);
    };
    window.addEventListener('speedotube_open_legal_modal', handleOpenModal);
    return () => window.removeEventListener('speedotube_open_legal_modal', handleOpenModal);
  }, []);

  // Close modal on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeModalTab) {
        setActiveModalTab(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeModalTab]);

  const openLegalModal = (tab) => {
    setActiveModalTab(tab);
  };

  const closeModal = () => {
    setActiveModalTab(null);
  };

  const handleNav = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new Event('popstate'));
    }
  };

  return (
    <footer className="st-footer-wrapper">
      <div className="st-footer-container">
        {/* Single Line Footer: Copyright Statement + Legal Links */}
        <div className="st-footer-bottom-bar">
          <div className="st-footer-bottom-left">
            <span className="st-footer-copy-text">
              © {new Date().getFullYear()} SpeedoTube INC. All Rights Reserved.
            </span>
          </div>

          <div className="st-footer-bottom-right">
            <button
              type="button"
              className="st-footer-bottom-link"
              onClick={() => openLegalModal('about')}
            >
              About Us
            </button>
            <span className="st-footer-bullet">•</span>
            <button
              type="button"
              className="st-footer-bottom-link"
              onClick={() => openLegalModal('privacy')}
            >
              Privacy Policy
            </button>
            <span className="st-footer-bullet">•</span>
            <button
              type="button"
              className="st-footer-bottom-link"
              onClick={() => openLegalModal('terms')}
            >
              Terms & Conditions
            </button>
            <span className="st-footer-bullet">•</span>
            <span className="st-footer-region-badge">🇮🇳 India • English</span>
          </div>
        </div>
      </div>

      {/* Interactive Legal & Information Modal */}
      {activeModalTab && (
        <div className="st-legal-modal-backdrop" onClick={closeModal}>
          <div className="st-legal-modal-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="st-legal-modal-header">
              <div className="st-legal-tabs-row">
                <button
                  type="button"
                  className={`st-legal-tab-btn ${activeModalTab === 'about' ? 'active' : ''}`}
                  onClick={() => setActiveModalTab('about')}
                >
                  <MdInfo size={17} />
                  <span>About Us</span>
                </button>
                <button
                  type="button"
                  className={`st-legal-tab-btn ${activeModalTab === 'privacy' ? 'active' : ''}`}
                  onClick={() => setActiveModalTab('privacy')}
                >
                  <MdSecurity size={17} />
                  <span>Privacy Policy</span>
                </button>
                <button
                  type="button"
                  className={`st-legal-tab-btn ${activeModalTab === 'terms' ? 'active' : ''}`}
                  onClick={() => setActiveModalTab('terms')}
                >
                  <MdGavel size={17} />
                  <span>Terms & Conditions</span>
                </button>
              </div>

              <button
                type="button"
                className="st-legal-modal-close-btn"
                onClick={closeModal}
                title="Close (Esc)"
                aria-label="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            {/* Modal Scrollable Content */}
            <div className="st-legal-modal-body">
              {activeModalTab === 'about' && (
                <div className="st-legal-content-section">
                  <div className="st-legal-hero-banner">
                    <span className="st-legal-eyebrow">SPEEDOTUBE INC • CORPORATE OVERVIEW</span>
                    <h2>About SpeedoTube INC</h2>
                    <p className="st-legal-lead">
                      Empowering creators, storytellers, musicians, and millions of passionate viewers worldwide with high-fidelity video streaming and community engagement.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>1. Our Vision & Mission</h3>
                    <p>
                      SpeedoTube INC was founded to revolutionize digital video distribution. We believe in open access to knowledge, entertaining narratives, high-resolution original music, and empowering independent creators with fair, transparent monetization tools.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>2. Streaming Architecture & Technology</h3>
                    <p>
                      SpeedoTube utilizes modern streaming protocols including adaptive HLS (HTTP Live Streaming) packaged via Shaka Packager with multi-bitrate profiles (from 144p mobile data saver up to 4K Ultra HD 60 FPS). Our backend leverages fluent-ffmpeg with Constant Rate Factor (CRF 28) compression to ensure silky-smooth playback even on fluctuating mobile cellular networks.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>3. Music & Creator Ecosystem</h3>
                    <p>
                      From full-length original studio albums with persistent background audio miniplayers to 60-second vertical Shorts, SpeedoTube provides creators with comprehensive analytics, custom channel handles, comment moderation, and real-time interaction tools.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>4. Corporate Headquarters & Contact</h3>
                    <div className="st-contact-card">
                      <div className="st-contact-row">
                        <MdOutlineLocationOn size={18} />
                        <span>SpeedoTube INC Headquarters • Cyber Tech Park, India</span>
                      </div>
                      <div className="st-contact-row">
                        <MdOutlineEmail size={18} />
                        <span>Corporate Relations: legal@speedotube.in | support@speedotube.in</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeModalTab === 'privacy' && (
                <div className="st-legal-content-section">
                  <div className="st-legal-hero-banner">
                    <span className="st-legal-eyebrow">LEGAL POLICY • LAST UPDATED SEPTEMBER 2026</span>
                    <h2>SpeedoTube Privacy Policy</h2>
                    <p className="st-legal-lead">
                      Your privacy, data sovereignty, and security are fundamental principles at SpeedoTube INC.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>1. Information We Collect</h3>
                    <p>
                      We collect account information (username, email, channel name, avatar), watch history, search queries, liked videos, and technical telemetry (device resolution, network bitrate) solely to deliver optimized adaptive video streaming and personalize your content feed.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>2. How We Protect Your Data</h3>
                    <p>
                      SpeedoTube INC enforces enterprise-grade TLS 1.3 encryption for all data in transit, bcrypt hashed password storage, and secure tokenized authentication. We do not sell your personal identifying data to third-party data brokers.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>3. Cookies and Local Storage</h3>
                    <p>
                      We use browser local storage and essential cookies strictly to remember your preferences (such as Dark/Light theme, Autonav playback toggle, playback speed, and active channel session).
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>4. Third-Party Advertisements & Compliance</h3>
                    <p>
                      Advertisements served through the SpeedoTube CRM Campaign Manager adhere strictly to privacy guidelines. We do not track users across non-affiliated third-party websites. Viewers have the right to request access to or deletion of their account history at any time.
                    </p>
                  </div>
                </div>
              )}

              {activeModalTab === 'terms' && (
                <div className="st-legal-content-section">
                  <div className="st-legal-hero-banner">
                    <span className="st-legal-eyebrow">USER AGREEMENT • SPEEDOTUBE INC</span>
                    <h2>Terms & Conditions of Service</h2>
                    <p className="st-legal-lead">
                      By accessing or using SpeedoTube services, website, and APIs, you agree to be bound by these Terms of Service.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>1. Acceptance of Terms</h3>
                    <p>
                      These Terms govern your use of SpeedoTube INC software, web application, mobile interfaces, and digital content. If you do not agree to these terms, you must discontinue using the platform.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>2. Intellectual Property & Copyright (All Rights Reserved)</h3>
                    <p>
                      All software, video players, algorithms, branding, logos, graphics, and interface designs are the exclusive property of <strong>SpeedoTube INC. All Rights Reserved</strong>. Creators retain copyright ownership of the original audio-visual material they upload, while granting SpeedoTube INC a worldwide license to host, transcode, cache, and broadcast said content to viewers.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>3. Acceptable Use Policy & Community Guidelines</h3>
                    <p>
                      Users agree not to upload content that is illegal, defamatory, infringes on copyright or trademarks, contains malicious code, or promotes hate speech. Violations will result in immediate content removal, strike issuance, or permanent channel termination.
                    </p>
                  </div>

                  <div className="st-legal-block">
                    <h3>4. Limitation of Liability</h3>
                    <p>
                      SpeedoTube INC provides the service "as is" and "as available". While we strive for 99.9% uptime and optimal streaming throughput, SpeedoTube INC is not liable for intermittent network disruptions, third-party ISP outages, or unauthorized account access resulting from compromised user credentials.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="st-legal-modal-footer">
              <span className="st-legal-footer-note">
                Official Document Issued by SpeedoTube INC Legal & Compliance Division
              </span>
              <button
                type="button"
                className="st-legal-done-btn"
                onClick={closeModal}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
